import { useMountEffect } from "@react-hookz/web";
import type { FunctionComponent, ReactNode } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { TopPicksWidgetItem } from "~/components/article/article-left-hand-side-widgets/top-picks-widget/top-picks-widget-item";
import type { AiTopPicksResponse } from "~/lib/ai-engine/types";
import { sortArrayByReferenceOrder } from "~/lib/utils";
import type { topPicksWidgetDisplayArticleQuery } from "~/queries/__generated__/topPicksWidgetDisplayArticleQuery.graphql";

import { useTracking } from "./hooks";
import { Container, Header } from "./styles";

export type Props = {
  aiData: AiTopPicksResponse;
  className?: string;
  maximumHeight: number | undefined;
  slots?: {
    footer?: { estimatedHeight: number; node: ReactNode };
    header?: { estimatedHeight: number; node: ReactNode };
  };
};

export const TopPicksWidgetDisplay: FunctionComponent<Props> = ({
  aiData,
  className,
  maximumHeight,
  slots,
}) => {
  const { trackClick, trackImpression } = useTracking();

  const data = useLazyLoadQuery<topPicksWidgetDisplayArticleQuery>(
    graphql`
      query topPicksWidgetDisplayArticleQuery($entityIds: [String], $first: Int!) {
        articles(filter: { entityIds: $entityIds }, first: $first) {
          edges {
            node {
              ...topPicksWidgetItemArticle
              entityId
            }
          }
        }
      }
    `,
    {
      entityIds: aiData.results.map(result => result.id) ?? [],
      first: aiData.results.length ?? 0,
    },
    { fetchPolicy: "store-or-network" },
  );

  const orderedArticles = sortArrayByReferenceOrder({
    data: { array: data.articles.edges ?? [], idGetter: item => item.node.entityId },
    order: { array: aiData.results, idGetter: item => item.id },
  });

  useMountEffect(() => {
    trackImpression(
      aiData.rec_id,
      aiData.results.map(({ id }) => id),
    );
  });

  if (orderedArticles.length <= 0 || maximumHeight === undefined) return null;

  const limitedItemCount = Math.max(
    Math.round(
      (maximumHeight -
        (slots?.header?.estimatedHeight ?? 0) -
        (slots?.footer?.estimatedHeight ?? 0)) /
        250,
    ),
    1,
  );

  return (
    <Container className={className}>
      <Header>Top Picks</Header>
      {slots?.header?.node}
      {orderedArticles.slice(0, limitedItemCount).map(({ node }) => (
        <TopPicksWidgetItem
          key={node.entityId}
          reference={node}
          onClick={() => trackClick(aiData.rec_id, node.entityId)}
        />
      ))}
      {slots?.footer?.node}
    </Container>
  );
};

TopPicksWidgetDisplay.displayName = "TopPicksWidgetDisplay";
